// Hızlı Satış / Restoran Ekran Ayarları penceresi kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'sales.screenSettings';

export const screenSettingsGuide = (t: TFunction): GuideContent => ({
  layout: 'tabbed',
  title: t(`${G}.title`, 'Satış ekranı ayarları nasıl yapılır?'),
  intro: t(`${G}.intro`, 'Bu pencerede bir hızlı satış veya restoran ekranının ödeme yöntemlerini, personelini, ürünlerini, yazıcılarını ve ek özelliklerini ayarlarsınız. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  regionLabels: {
    header: t(`${G}.regions.header`, 'Başlık'),
    tabs: t(`${G}.regions.tabs`, 'Ayar sekmeleri'),
    body: t(`${G}.regions.body`, 'Seçili sekmenin ayarları'),
    footer: t(`${G}.regions.footer`, 'İptal / Kaydet'),
  },
  navItems: [
    t(`${G}.nav.paymentMethods`, 'Ödeme Yöntemleri'),
    t(`${G}.nav.personnel`, 'Login İşlemleri'),
    t(`${G}.nav.cashier`, 'Kasiyer Atama'),
    t(`${G}.nav.warehouse`, 'Depo/Stok'),
    t(`${G}.nav.printing`, 'Yazdırma'),
    t(`${G}.nav.extra`, 'Ek Ayarlar'),
    t(`${G}.nav.localService`, 'Yerel Servis'),
    t(`${G}.nav.customerDisplay`, 'Müşteri Ekranı'),
    t(`${G}.nav.devices`, 'Cihazlar'),
    t(`${G}.nav.itemNotes`, 'Ürün Açıklamaları'),
  ],
  steps: [
    {
      region: 'header',
      title: t(`${G}.s1.title`, '1. Bu pencere ne işe yarar?'),
      text: t(`${G}.s1.text`, 'Başlıkta ayarladığınız ekranın adı yazar. Hızlı satış ekranında başlık mavi, restoran ekranında turuncudur. Sekmeler ekran tipine göre değişir.'),
      tips: [
        t(`${G}.s1.tip1`, 'Restoran ekranında ek olarak Masa Grupları, Masalar ve Garson Atama sekmeleri vardır.'),
        t(`${G}.s1.tip2`, 'Kulüp Girişi sekmesi yalnız hızlı satış ekranında, Entegrasyonlar sekmesi yalnız paket sipariş ekranında görünür.'),
        t(`${G}.s1.tip3`, 'Telefonda sekmeler üstteki "Değiştir" düğmesiyle açılan listeden seçilir.'),
      ],
    },
    {
      region: 'tabs',
      title: t(`${G}.s2.title`, '2. Ödeme Yöntemleri'),
      text: t(`${G}.s2.text`, 'Kasada görünecek ödeme düğmelerini "+ Nakit Ekle", "+ POS Ekle", "+ Açık Hesap Ekle" ve "Diğer Ödeme Ekle" ile tanımlayın. Nakit ödemeye bir kasa, POS ödemesine bir POS hesabı seçin: satışın parası bu kasaya veya POS hesabına yazılır. Aynı kasa ya da POS hesabı iki ayrı düğmeye seçilemez.'),
      tips: [
        t(`${G}.s2.tip1`, 'Açık Hesap seçildiğinde müşteri (cari) satış sırasında seçilir.'),
        t(`${G}.s2.tip2`, 'Yazarkasa Ayarları: Bağlantı Uyarısı açıksa yazarkasa bağlı değilken nakit ve POS ödemelerinde uyarı çıkar; kapalıysa ödeme uyarısız devam eder.'),
        t(`${G}.s2.tip3`, 'Para Üstü Modalı ve Ön Ödemeli Sistem de bu sekmeden açılıp kapatılır.'),
      ],
    },
    {
      region: 'tabs',
      title: t(`${G}.s3.title`, '3. Personel ve kasiyerler'),
      text: t(`${G}.s3.text`, 'Login İşlemleri sekmesinde bu ekranı kullanabilecek personeli seçin, sonra soldan bir personel seçip yetkilerini verin: Fiyat Değiştirme, İndirim, İptal, Rapor Görüntüleme, Depo İşlemleri, Cari Bakiye Görüntüleme, Cari Listeleme, Hızlı Stok Kaydı ve Cari Kaydı.'),
      tips: [
        t(`${G}.s3.tip1`, 'Kasiyer Atama: "Kasiyer Sistemi Aktif" açıksa kasiyerler bu ekranda giriş yapmadan satış yapamaz. Kasiyer eklemek isteğe bağlıdır.'),
        t(`${G}.s3.tip2`, '"Cari Tahsilat Yapılabilsin" bu ekranda (ve bağlı Pavo cihazlarında) cariden tahsilat almayı açar; kasiyer bazında ayrıca izin verilmelidir.'),
        t(`${G}.s3.tip3`, 'Restoran ekranında garsonlar Garson Atama sekmesinden eklenir.'),
      ],
    },
    {
      region: 'tabs',
      title: t(`${G}.s4.title`, '4. Depo/Stok ve Yazdırma'),
      text: t(`${G}.s4.text`, 'Depo/Stok sekmesinde "Depo Seç" ile ürünlerin listeleneceği depoları seçin, her depo için gösterilecek kategorileri ve ürünleri belirleyin. Yazdırma sekmesinde "Tek Yazıcı Kullan" ya da "Kategori Bazlı Yazıcı" modunu seçip yazıcıları atayın.'),
      tips: [
        t(`${G}.s4.tip1`, 'Bu sekmede "Stoklar eksiye düşmesin" ve "Ürün resimleri gösterilsin" seçenekleri de bulunur.'),
        t(`${G}.s4.tip2`, 'Fiş görünümü "Adisyon Yazdırma Ayarları" düğmesinden ayarlanır; "Fiş No Sıfırla" bu bilgisayardaki fiş numarasını 1\'e döndürür.'),
        t(`${G}.s4.tip3`, 'Yerel Servis sekmesi yazdırma ve yazarkasa için gereken servisin adresidir; "Bağlantıyı Test Et" ile kontrol edin.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s5.title`, '5. Ek Ayarlar'),
      text: t(`${G}.s5.text`, 'Ek Ayarlar, satış ekranının kendisini değil çevresindeki yardımcı özellikleri yönetir: Mutfak Entegrasyonu (satışları mutfak ekranına gönderir), Görünüm Ayarları Butonu (kasadaki ekran arayüz ayarları düğmesini gösterir/gizler) ve Oto Kilitleme.'),
      tips: [
        t(`${G}.s5.tip1`, 'Mutfak Entegrasyonu açıksa bir QR menü seçmek zorunludur; seçilmezse kayıt yapılmaz.'),
        t(`${G}.s5.tip2`, 'Oto Kilitleme yalnız kasiyer veya garson sistemi açıkken çalışır: belirtilen süre ekrana dokunulmazsa oturum kapanır, son 5 saniyede geri sayım görünür.'),
        t(`${G}.s5.tip3`, 'Restoran ekranında "Hazır Ürün Silme Nedenleri" de buradadır; personel ürün silerken bu nedenlerden birini seçer.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s6.title`, '6. Müşteri ekranı, cihazlar ve ürün açıklamaları'),
      text: t(`${G}.s6.text`, 'Müşteri Ekranı ikinci ekranda reklam ve sepeti gösterir. Cihazlar sekmesinden bu ekrana bağlı Pavo cihazları, Kiosk Cihazları sekmesinden kiosklar eklenir. Ürün Açıklamaları sekmesindeki hazır açıklamalar, sepette ürün seçilip "Açıklama" düğmesine basılınca tek dokunuşla eklenir.'),
      tips: [
        t(`${G}.s6.tip1`, 'Ürün açıklaması listesi boşsa açıklama penceresinde yalnız klavye görünür. En fazla 40 açıklama, her biri en fazla 60 karakter olabilir.'),
        t(`${G}.s6.tip2`, 'Pavo cihazı eklemek için önce ekran ayarlarını kaydetmelisiniz. Bir kiosk yalnız tek bir ekrana bağlı olabilir.'),
      ],
    },
    {
      region: 'footer',
      title: t(`${G}.s7.title`, '7. Değişiklikleri kaydedin'),
      text: t(`${G}.s7.text`, '"Değişiklikleri Kaydet" tüm sekmelerdeki değişiklikleri birlikte kaydeder ve yalnız bir değişiklik yaptığınızda açılır. "İptal" kaydetmeden kapatır. Sağ üstteki (i) bu rehberi yeniden açar.'),
    },
  ],
});
